import { Button, StepContent } from '@mui/material';
import Box from '@mui/material/Box';
import Step, { StepProps } from '@mui/material/Step';
import StepLabel, { StepLabelProps } from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { Stack } from '@mui/system';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import { FunctionComponent, ReactNode, useState } from 'react';

export type WizardProps = {
  steps: {
    content: ReactNode;
    isEditable?: () => boolean;
    isValid?: () => boolean;
    labelContent: ReactNode;
    labelProps?: StepLabelProps;
    stepProps?: StepProps;
    onDone?: () => void;
  }[];
};

export const Wizard: FunctionComponent<WizardProps> = (props) => {
  const { steps } = props;
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    const onDone = steps[activeStep]?.onDone;
    if (onDone) {
      onDone();
    }
    if (activeStep < steps.length) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="column" spacing={3}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map(
            (
              {
                content,
                isEditable,
                isValid,
                labelContent,
                labelProps,
                stepProps,
              },
              index,
            ) => {
              let optional: ReactNode | undefined;
              const isStepValid = isValid && isValid();
              const isStepEditable = isEditable && isEditable();
              if (labelProps?.optional) {
                optional = (
                  <Stack direction="row" spacing={1}>
                    <Box>{labelProps.optional}</Box>
                    {isStepEditable && isStepValid && (
                      <Box
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setActiveStep(index)}
                      >
                        <EditSharpIcon fontSize="small" />
                      </Box>
                    )}
                  </Stack>
                );
              }
              return (
                <Step key={index} {...stepProps}>
                  <StepLabel {...labelProps} optional={optional}>
                    {labelContent}
                  </StepLabel>
                  <StepContent>
                    <Stack direction="column">
                      <Box>{content}</Box>
                      <Box
                        sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}
                      >
                        {activeStep > 0 && (
                          <Button
                            color="inherit"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{ mr: 1 }}
                            variant="outlined"
                          >
                            Back
                          </Button>
                        )}
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button
                          disabled={!isStepValid}
                          onClick={handleNext}
                          variant="outlined"
                        >
                          {activeStep === steps.length - 1
                            ? 'Done'
                            : 'Continue'}
                        </Button>
                      </Box>
                    </Stack>
                  </StepContent>
                </Step>
              );
            },
          )}
        </Stepper>
      </Stack>
    </Box>
  );
};
